import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import useBlockStyles from '../common/useBlockStyles';
import { resolveLink, isInternalLink } from '../../../../utils/linkResolver';

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'xlarge',
  defaultAlignment: 'center',
  defaultButtonStyle: 'primary',
  defaultPadding: 'spacious',
};

/**
 * BannerRenderer - Render component for hero block
 * Fits remaining viewport height with image alignment options
 * Uses blur-fill background to cover empty space around contained images
 */
const BannerRenderer = ({ block, theme = {}, username }) => {
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const { content } = block;
  const {
    heading,
    subheading,
    buttonText,
    buttonLink,
    backgroundImage,
    imageAlignment = 'center',
    overlayOpacity = 0.5,
  } = content;

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);
  const isOwn = !window.location.pathname.startsWith('/me/');
  const resolvedHref = resolveLink(buttonLink, username, isOwn);
  const useRouterLink = isInternalLink(resolvedHref);

  // Resolve text position (9-cell grid). Back-compat: legacy `alignment` ('left'|'center'|'right')
  // and `style.headingAlignment` map to the middle row.
  const getTextPosition = () => {
    if (content.textPosition) return content.textPosition;
    const legacy = content.style?.headingAlignment || content.alignment || styles.headingAlignment;
    if (legacy === 'left') return 'middle-left';
    if (legacy === 'right') return 'middle-right';
    return 'middle-center';
  };
  const textPosition = getTextPosition();
  const [vPos, hPos] = textPosition.split('-'); // top|middle|bottom, left|center|right

  const verticalJustifyClass = {
    top: 'justify-start',
    middle: 'justify-center',
    bottom: 'justify-end',
  }[vPos] || 'justify-center';

  const horizontalItemsClass = {
    left: 'items-start text-left',
    center: 'items-center text-center',
    right: 'items-end text-right',
  }[hPos] || 'items-center text-center';

  // For constrained-width elements (subheading) to follow horizontal alignment
  const subheadingAlignClasses = {
    left: '',
    center: 'mx-auto',
    right: 'ml-auto',
  };
  const subheadingAlignClass = subheadingAlignClasses[hPos] || '';

  // Map imageAlignment to object-position for the sharp image
  const imageObjectPositionMap = {
    left: 'left center',
    center: 'center center',
    right: 'right center',
  };

  const hasBackground = !!backgroundImage;

  // With background image: fit remaining viewport height (minus header ~64px)
  if (hasBackground) {
    const blockId = block.id || 'banner';

    return (
      <>
        <style>{`
          .banner-hero-${blockId} {
            height: calc(100vh - 64px);
            min-height: 400px;
          }
          @media (max-width: 768px) {
            .banner-hero-${blockId} {
              height: auto;
            }
            .banner-hero-${blockId} .banner-sizer {
              display: block;
            }
            .banner-hero-${blockId} .banner-sharp {
              object-fit: cover !important;
            }
          }
        `}</style>
        <div
          className={`relative w-full overflow-hidden banner-hero-${blockId}`}
        >
          {/* Sizer: invisible inline image to set natural height on mobile */}
          <img
            src={backgroundImage}
            alt=""
            className="banner-sizer w-full hidden"
            style={{ visibility: 'hidden' }}
          />

          {/* Layer 1: Blur-fill background — covers entire container */}
          <img
            src={backgroundImage}
            alt=""
            className="absolute inset-0 w-full h-full"
            style={{
              objectFit: 'cover',
              objectPosition: 'center',
              filter: 'blur(25px)',
              transform: 'scale(1.1)',
              opacity: imageLoaded ? 1 : 0,
            }}
          />

          {/* Layer 1.5: Gradient vignette — darkens blur edges for smooth transition */}
          <div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.25) 100%)',
            }}
          />

          {/* Layer 2: Sharp image — contained on desktop, cover on mobile */}
          <img
            src={backgroundImage}
            alt=""
            className="absolute inset-0 w-full h-full banner-sharp"
            onLoad={() => setImageLoaded(true)}
            style={{
              objectFit: 'contain',
              objectPosition: imageObjectPositionMap[imageAlignment] || 'center center',
              opacity: imageLoaded ? 1 : 0,
              WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent), linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)',
              WebkitMaskComposite: 'destination-in',
              maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent), linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)',
              maskComposite: 'intersect',
            }}
          />

          {/* Layer 3: Dark overlay */}
          <div
            className="absolute inset-0"
            style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity})` }}
          />

          {/* Layer 4: Content overlay */}
          <div
            className={`absolute inset-0 z-10 px-8 sm:px-12 md:px-16 lg:px-24 py-10 sm:py-14 md:py-16 lg:py-20 flex flex-col ${verticalJustifyClass} ${horizontalItemsClass}`}
          >
            <div className="max-w-4xl w-full">
              {heading && (
                <h2
                  className={`${styles.headingSizeClass} font-bold mb-3 sm:mb-4 leading-tight`}
                  style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
                >
                  {heading}
                </h2>
              )}

              {subheading && (
                <p
                  className={`text-xl sm:text-2xl md:text-3xl font-bold mb-6 sm:mb-8 max-w-2xl opacity-80 whitespace-pre-wrap ${subheadingAlignClass}`}
                  style={{ color: styles.bodyColor, fontFamily: styles.bodyFontStyle }}
                >
                  {subheading}
                </p>
              )}

              {buttonText && (
                useRouterLink ? (
                  <Link
                    to={resolvedHref}
                    className="inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3 min-h-[44px] text-white font-semibold rounded-lg transition-all hover:opacity-90 text-sm sm:text-base"
                    style={styles.getButtonStyles()}
                  >
                    {buttonText}
                    <ArrowRight size={18} />
                  </Link>
                ) : (
                  <a
                    href={resolvedHref}
                    className="inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3 min-h-[44px] text-white font-semibold rounded-lg transition-all hover:opacity-90 text-sm sm:text-base"
                    style={styles.getButtonStyles()}
                  >
                    {buttonText}
                    <ArrowRight size={18} />
                  </a>
                )
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // Without background image: use gradient with min-height
  const pageBackground = theme.colors?.pageBackground || '#0d0d0d';

  return (
    <div className="relative w-full overflow-hidden">
      <div
        className={`min-h-[300px] sm:min-h-[350px] md:min-h-[400px] flex flex-col ${verticalJustifyClass} ${horizontalItemsClass} ${styles.sectionPaddingClass}`}
        style={{
          background: `linear-gradient(to bottom right, ${styles.sectionBackground}, ${pageBackground})`,
        }}
      >
        <div className="max-w-4xl w-full">
          {heading && (
            <h2
              className={`${styles.headingSizeClass} font-bold mb-3 sm:mb-4 leading-tight`}
              style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
            >
              {heading}
            </h2>
          )}

          {subheading && (
            <p
              className={`text-base sm:text-lg md:text-xl mb-6 sm:mb-8 max-w-2xl opacity-80 whitespace-pre-wrap ${subheadingAlignClass}`}
              style={{ color: styles.bodyColor, fontFamily: styles.bodyFontStyle }}
            >
              {subheading}
            </p>
          )}

          {buttonText && (
            useRouterLink ? (
              <Link
                to={resolvedHref}
                className="inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3 min-h-[44px] text-white font-semibold rounded-lg transition-all hover:opacity-90 hover:transform hover:scale-105 text-sm sm:text-base"
                style={styles.getButtonStyles()}
              >
                {buttonText}
                <ArrowRight size={18} />
              </Link>
            ) : (
              <a
                href={resolvedHref}
                className="inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3 min-h-[44px] text-white font-semibold rounded-lg transition-all hover:opacity-90 hover:transform hover:scale-105 text-sm sm:text-base"
                style={styles.getButtonStyles()}
              >
                {buttonText}
                <ArrowRight size={18} />
              </a>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default BannerRenderer;
